import { TFetchConverter } from 'data/types/converter.types';
import { TTableFilterDataUI } from 'data/types/generic.types';
import {
  TInputOutputRow,
  TIoTransactionsFiltersDB,
  TIoTransactionsFiltersUI,
} from 'data/types/io.types';

type TNamedEntityDB = {
  resourceId?: string;
  name?: string | null;
  displayName?: string | null;
};

type TIoTransactionDB = {
  id: string;
  date?: string; // YYYY-MM-DD
  type?: 'MONEY' | 'MATERIALS' | 'TIME';
  value?: string | null;
  notes?: string | null;
  giverType?: string | null;
  giverDisplayName?: string | null;
  giverId?: string | null;
  receiverType?: string | null;
  receiverDisplayName?: string | null;
  receiverId?: string | null;
  categoryName?: string | null;
  categoryId?: string | null;
  document?: { url: string; name: string } | null;
  // Event/Workshop specific fields (based on API schema)
  giverHost?: TNamedEntityDB | null;
  giverLocation?: TNamedEntityDB | null;
  receiverHost?: TNamedEntityDB | null;
  receiverLocation?: TNamedEntityDB | null;
  canEdit?: boolean;
  isInkindDonation?: boolean;
  inkindDollarValue?: number | null;
  timeHours?: number | null;
  timeMinutes?: number | null;
  isExternalEvent?: boolean | null;
  externalEventName?: string | null;
  externalEventHost?: string | null;
  externalEventZip?: string | null;
  familiesServed?: number | null;
};

const formatDate = (iso?: string): string => {
  if (!iso) return '';
  const parts = iso.split('-');
  if (parts.length !== 3) return iso;
  const [y, m, d] = parts;
  return `${m}/${d}/${y}`;
};

const mapTypeToUi = (
  t?: 'MONEY' | 'MATERIALS' | 'TIME',
): TInputOutputRow['type'] => {
  switch (t) {
    case 'MONEY':
      return 'Money';
    case 'MATERIALS':
      return 'Materials';
    case 'TIME':
      return 'Time';
    default:
      return 'Materials';
  }
};

const mapValueToAmount = (
  t?: 'MONEY' | 'MATERIALS' | 'TIME',
  value?: string | null,
): string => {
  if (!value) return '';
  if (t === 'TIME') {
    // If numeric like "10", show as HH:mm
    if (/^\d+$/.test(value)) return `${value}:00`;
    return value;
  }
  return String(value);
};

export const ioTransactionGetConverter = {
  fromDb(db: TIoTransactionDB): TInputOutputRow {
    const typeUi = mapTypeToUi(db.type);
    const doc = db.document || null;
    const docName = doc?.name || '';
    const docUrl = doc?.url || '';
    const docIsSvg = !!(
      docName.toLowerCase().endsWith('.svg') ||
      docUrl.toLowerCase().endsWith('.svg')
    );
    const hostName =
      db.giverHost?.displayName ||
      db.receiverHost?.displayName ||
      db.giverHost?.name ||
      db.receiverHost?.name ||
      '';
    const locationName =
      db.giverLocation?.displayName ||
      db.receiverLocation?.displayName ||
      db.giverLocation?.name ||
      db.receiverLocation?.name ||
      '';

    const eventOrWorkshopName = hostName || locationName || '';
    let hostOrLocation: 'HOST' | 'LOCATION' | undefined;
    if (hostName) hostOrLocation = 'HOST';
    else if (locationName) hostOrLocation = 'LOCATION';

    return {
      id: db.id,
      date: formatDate(db.date),
      giving: db.giverDisplayName || '',
      giverId: db.giverId || undefined,
      giverType: (db.giverType as TInputOutputRow['giverType']) || undefined,
      receiving: db.receiverDisplayName || '',
      receiverId: db.receiverId || undefined,
      receiverType:
        (db.receiverType as TInputOutputRow['receiverType']) || undefined,
      type: typeUi,
      rawType: db.type,
      category: db.categoryName || '',
      categoryId: db.categoryId || undefined,
      amount: mapValueToAmount(db.type, db.value),
      rawValue: db.value ?? null,
      notes: db.notes || '',
      document: !!doc,
      documentName: docName || undefined,
      documentUrl: docUrl || undefined,
      documentIsSvg: doc ? docIsSvg : undefined,
      canEdit: db.canEdit ?? undefined,
      isInkindDonation: db.isInkindDonation ?? undefined,
      inkindDollarValue: db.inkindDollarValue ?? null,
      timeHours: db.timeHours ?? null,
      timeMinutes: db.timeMinutes ?? null,
      isExternalEvent: !!db.isExternalEvent,
      externalEventName: db.externalEventName ?? null,
      externalEventHost: db.externalEventHost ?? null,
      externalEventZip: db.externalEventZip ?? null,
      familiesServed: db.familiesServed ?? null,
      ...(eventOrWorkshopName && { eventOrWorkshopName }),
      ...(hostOrLocation && { hostOrLocation }),
    };
  },
};

export const ioTransactionsFilterConverter = (
  filters?: Record<
    string,
    import('antd/es/table/interface').FilterValue | null
  >,
) => {
  if (!filters) return {} as Record<string, string>;
  const toCsv = (v?: import('antd/es/table/interface').FilterValue | null) => {
    if (!v) return undefined;
    if (Array.isArray(v)) return v.filter(Boolean).join(',');
    return String(v);
  };

  const categoryIds = toCsv(filters.category);
  const types = toCsv(filters.type);
  const receiverIds = toCsv(filters.RECEIVER);
  const giverIds = toCsv(filters.GIVER);
  const inKindRaw = toCsv(filters.isInkindDonation);
  const inKind = inKindRaw === 'true' ? true : undefined;

  return {
    ...(categoryIds && { categoryIds }),
    ...(types && { types }),
    ...(receiverIds && { receiverIds }),
    ...(giverIds && { giverIds }),
    ...(inKind !== undefined && { inKind }),
  };
};

const mapRecordToFilterItems = (
  record?: Record<string, string>,
): TTableFilterDataUI[] => {
  const entries = Object.entries(record || {});
  return entries.map(([value, text]) => ({ text, value }));
};

export const ioTransactionsFiltersConverter: TFetchConverter<
  TIoTransactionsFiltersUI,
  TIoTransactionsFiltersDB
> = {
  fromDb: data => ({
    givers: mapRecordToFilterItems(data?.givers),
    receivers: mapRecordToFilterItems(data?.receivers),
  }),
};
